/**
 * TopKunden — the Top-5-Kunden list (Story UI-U7, AC 6).
 *
 * Hairline-divided rows: the name is an EntityLink → the Kunden detail route
 * (/kunden/$id, resolved by subject_id — the CustomersScreen precedent), blue ONLY
 * because it is genuinely clickable. Policen count muted; Prämie mono right (or `—`
 * when gated). Ranked by premium (by policy count when premium is gated — the server
 * decides the order; the UI renders it).
 *
 * [Source: apps/web/src/features/entity-detail/hooks/useEntityDetail.ts] (/kunden/$id = subject_id)
 * [Source: apps/web/src/components/ui/EntityLink.tsx]
 * [Source: docs/stories/ui-u7-portfolio.md AC 6]
 */

import { useNavigate } from '@tanstack/react-router';
import type { TopKundeDto } from '@brokly/shared';
import { Card } from '../../../components/ui/Card';
import { EntityLink } from '../../../components/ui/EntityLink';
import { chfOrDash } from '../util';

export function TopKunden({ kunden }: { kunden: TopKundeDto[] }) {
  const navigate = useNavigate();

  return (
    <Card className="rounded-xl p-4">
      <div className="mb-3 text-sm font-medium">Top-Kunden</div>
      {kunden.length === 0 ? (
        <div className="grid h-32 place-items-center text-sm text-muted-foreground">
          Keine Kundendaten.
        </div>
      ) : (
        <ul className="divide-y divide-border">
          {kunden.map((k) => (
            <li key={`${k.subject_type}-${k.subject_id}`} className="flex items-center gap-3 py-2.5">
              <div className="min-w-0 flex-1">
                <EntityLink
                  onActivate={() =>
                    void navigate({ to: '/kunden/$id', params: { id: k.subject_id } })
                  }
                >
                  {k.name ?? 'Unbenannt'}
                </EntityLink>
                <div className="text-[13px] text-muted-foreground">Policen: {k.policen}</div>
              </div>
              <div className="mono-num text-sm font-medium text-foreground">
                {chfOrDash(k.praemie_chf)}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
